<template>
  <transition name="fade">
    <div v-if="props.show" class="select-auto">
      <div
        class="absolute w-screen h-screen bg-black opacity-70 inset-0 z-10"
        @click.stop="onModalToggle"
      />

      <div
        class="absolute inset-0 w-screen h-screen flex items-center justify-center"
      >
        <div
          class="relative w-screen mx-2 xl:min-w-[800px] xl:max-w-4xl h-[80vh] max-h-xl p-4 xl:mx-auto my-auto shadow-lg bg-white z-10 overflow-y-scroll"
        >
          <h2 class="text-xl xl:text-3xl font-bold text-black text-center">
            My Designs
          </h2>
          <div class="flex flex-wrap">
            <div
              v-for="design in designsWithPreview"
              :key="design.id"
              class="relative border-2 border-black w-full mb-10"
            >
              <div
                v-if="design.design_name"
                class="absolute left-0 top-0 p-3 bg-black rounded-br-lg text-white text-lg select-text z-10"
              >
                {{ design.design_name }}
                <small class="hidden">{{ design.id }}</small>
              </div>
              <div
                class="absolute right-0 top-0 py-3 px-4 bg-black rounded-bl-lg text-white z-10 flex gap-3"
              >
                <button
                  aria-label="Share Design"
                  class="hover:text-blue-400 transition-colors"
                  title="Copy share link"
                  @click.stop="handleShareDesign(design.id)"
                >
                  <i class="fas fa-share-alt" />
                </button>
                <button
                  aria-label="Delete Design"
                  class="hover:text-red-500 transition-colors"
                  @click.stop="handleDeleteDesign(design.id)"
                >
                  <i class="fas fa-trash" />
                </button>
              </div>
              <div v-if="design.isV1" class="w-full justify-items-center">
                <ModalV1DesignRenderer
                  :design="design"
                  :width="design.grid_properties?.width || 820"
                  :height="design.grid_properties?.height || 400"
                />
              </div>
              <div
                v-else-if="design.imageUrl"
                class="w-full justify-items-center"
              >
                <img
                  :src="design.imageUrl"
                  height="auto"
                  alt=""
                  class="max-w-content"
                />
              </div>
              <div
                v-else-if="design.base64Image"
                class="w-full justify-items-center"
              >
                <img
                  :src="design.base64Image"
                  height="auto"
                  alt=""
                  class="max-w-content"
                />
              </div>
              <div v-else class="w-full justify-items-center">
                <img
                  v-if="design.imagePreview"
                  :src="design.imagePreview"
                  :width="design.grid_properties?.width"
                  height="auto"
                  alt=""
                  class="max-w-content"
                />

                <div
                  v-if="!design.imagePreview"
                  class="flex flex-wrap border z-0"
                >
                  <v-stage
                    ref="stageRef"
                    :config="{
                      ...tempStageConfig,
                      id: design.id,
                    }"
                  >
                    <v-layer
                      :config="{ id: 'shapes-layer', name: 'shapes-layer' }"
                    >
                      <component
                        :is="getShapeComponent(shape.type)"
                        v-for="shape in design.shapeList"
                        :key="shape.id"
                        :config="shape"
                        :draggable="false"
                      />
                    </v-layer>
                  </v-stage>
                </div>
              </div>
              <div class="flex">
                <button
                  aria-label="Open in Design Studio"
                  class="w-1/2 bg-black text-white p-2 hover:bg-gray-800 transition-colors"
                  @click="handleOpenInStudio(design.id)"
                >
                  Open in Design Studio
                </button>
                <button
                  aria-label="Share Design"
                  class="w-1/2 bg-blue-600 text-white p-2 hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  @click="handleShareDesign(design.id)"
                >
                  <i class="fas fa-share-alt" />
                  <span>Share</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Off-screen v-stage: max 10 at a time to avoid crash -->
          <div
            v-if="designsNeedingPreviewBatch.length > 0"
            class="preview-stage-offscreen"
            aria-hidden="true"
          >
            <v-stage
              ref="stageRef"
              v-for="design in designsNeedingPreviewBatch"
              :key="design.id"
              :config="{
                ...tempStageConfig,
                id: design.id,
              }"
            >
              <v-layer
                :config="{
                  id: design.id,
                  name: `preview-layer-${design.id}`,
                }"
              >
                <component
                  :is="getShapeComponent(shape.type)"
                  v-for="shape in design.shapeList"
                  :key="shape.id"
                  :config="shape"
                  :draggable="false"
                />
              </v-layer>
            </v-stage>
          </div>

          <button
            v-if="nextPage > 1"
            aria-label="Load More Designs"
            class="bg-black text-white p-2"
            @click="loadDesignList"
          >
            Load More
          </button>
          <div
            v-if="nextPage <= 1 && !loadingStore.isLoading"
            class="w-full text-center text-base"
          >
            All Designs Loaded
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { getShapeComponent } from "~/components/CanvasGrid/helpers"
import { transformShapeForAdd } from "~/composables/shapes/transform"
import { SweetAlertIcon } from '~/types/sweetalert'

const { $toast, $swal } = useNuxtApp();
const gridStore = useGridStore();
const userStore = useUserStore();
const projectStore = useProjectStore();
const loadingStore = useLoadingStore();

const stageRef = ref(null);
const designList = ref([]);
const nextPage = ref(1);
const maxPreviewCount = 5;

const tempStageConfig = {
  width: 820,
  height: 400,
  pixelRatio: 1,
  scaleX: 0.5,
  scaleY: 0.5,
};

const emit = defineEmits(["modalToggle"]);
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

/** Designs that already have a preview: imagePreview, imageUrl, base64Image, or isV1 */
const designsWithPreview = computed(() =>
  designList.value.filter(
    (d) =>
      d.isV1 ||
      d.imageUrl ||
      d.base64Image ||
      d.imagePreview
  )
);

/** Designs that need preview generation (no image yet, have shapeList) */
const designsNeedingPreview = computed(() =>
  designList.value.filter(
    (d) =>
      !d.isV1 &&
      !d.imageUrl &&
      !d.base64Image &&
      !d.imagePreview &&
      d.shapeList?.length > 0
  )
);

/** Only first maxPreviewCount designs needing preview (limits v-stage count to avoid crash) */
const designsNeedingPreviewBatch = computed(() =>
  designsNeedingPreview.value.slice(0, maxPreviewCount)
);

const onModalToggle = () => {
  emit("modalToggle");
};

const handleShareDesign = async (designId) => {
  if (!designId) return;

  try {
    // Build the share URL
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/?import=${designId}`;

    // Copy to clipboard
    await navigator.clipboard.writeText(shareUrl);

    // Show success toast
    $toast.fire({
      icon: SweetAlertIcon.SUCCESS,
      text: 'Share link copied to clipboard!',
      timer: 2000,
      timerProgressBar: true,
    });
  } catch (error) {
    console.error('Error copying to clipboard:', error);

    // Fallback for older browsers or if clipboard API fails
    try {
      // Create a temporary textarea element
      const textarea = document.createElement('textarea');
      textarea.value = `${window.location.origin}/?import=${designId}`;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);

      $toast.fire({
        icon: SweetAlertIcon.SUCCESS,
        text: 'Share link copied to clipboard!',
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (fallbackError) {
      $toast.fire({
        icon: SweetAlertIcon.ERROR,
        text: 'Failed to copy link. Please try again.',
      });
    }
  }
};

const handleDeleteDesign = async (designId) => {
  if (!designId) return;

  try {
    const result = await $swal.fire({
      title: 'This cannot be undone',
      text: 'This cannot be undone',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      loadingStore.setLoading(true);
      await $fetch(`/api/designs/${designId}`, {
        method: 'DELETE'
      });

      // Remove the design from the list
      designList.value = designList.value.filter(design => design.id !== designId);

      $toast.fire({
        icon: SweetAlertIcon.SUCCESS,
        text: 'Design deleted successfully',
      });
    }
  } catch (error) {
    console.error('Error deleting design:', error);
    $toast.fire({
      icon: SweetAlertIcon.ERROR,
      text: 'Error deleting design',
    });
  } finally {
    loadingStore.setLoading(false);
  }
};

const handleOpenInStudio = async (designID) => {
  if (!designID) return;

  try {
    loadingStore.setLoading(true);
    const design = await projectStore.load(designID);
    if (!design) throw new Error("Design not found");
    gridStore.resetGrid();
    await gridStore.loadDesign(design);
    onModalToggle();
  } catch {
    $toast.fire({
      icon: SweetAlertIcon.ERROR,
      text: "Error loading design",
    });
  } finally {
    loadingStore.setLoading(false);
  }
};

const replaceCanvasToImage = () => {
  if (!stageRef.value) return;

  stageRef.value.forEach((ref) => {
    if (ref.imageUrl || ref.base64Image) return;
    const stage = ref.getStage();
    const dataURL = stage.toDataURL({
      pixelRatio: 1,
      width: 820,
      height: 400,
    });

    const design = designList.value.find(
      (design) => design.id === stage.getId()
    );

    if (!design) return;

    design.imagePreview = dataURL;
  });
};

const loadDesignList = async () => {
  loadingStore.setLoading(true);

  try {
    const response = await userStore.fetchDesigns(nextPage.value);
    designList.value = designList.value.concat(response.designs);
    nextPage.value =
      response.pagination.totalPages > response.pagination.page
        ? ++response.pagination.page
        : 1;
  } catch (e) {
    // fetchDesigns throws when there is no signed-in user. Without this the
    // modal stayed on its loading spinner forever, so clear the flag and
    // leave the list empty. Callers are expected to gate on auth, but this
    // keeps the modal usable if it is ever opened directly.
    console.warn("Could not load designs", e);
    designList.value = [];
    nextPage.value = 1;
    loadingStore.setLoading(false);
    return;
  }

  const transformedDesigns = designList.value.map(async (design) => {
    if (design.imagePreview) return design;

    try {
      const transformations = design.shapes.map(
        async (shape) => await transformShapeForAdd(shape)
      );
      const shapes = await Promise.all(transformations);
      design.shapeList = shapes;
    } catch (e) {
      console.warn("Error by transforming shapes", e);
    }

    return design;
  });

  designList.value = await Promise.all(transformedDesigns);
  loadingStore.setLoading(false);

  // Process previews in batches (only maxPreviewCount v-stages rendered at a time)
  function processPreviewBatch() {
    nextTick(() => {
      if (designsNeedingPreview.value.length === 0) return;
      replaceCanvasToImage();
      nextTick(processPreviewBatch);
    });
  }
  nextTick(processPreviewBatch);
};

onMounted(async () => {
  loadDesignList();
});
</script>

<style>
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms ease-out;
}

/* Single off-screen stage: one layer per design needing preview; not visible in layout */
.preview-stage-offscreen {
  position: absolute;
  left: -9999px;
  top: 0;
  width: 820px;
  height: 400px;
  pointer-events: none;
  overflow: hidden;
}
</style>
